<script setup lang="ts">
import { RotateCw } from 'lucide-vue-next';
import { computed, onMounted } from 'vue';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { formatDate } from '@/lib/commonFunctions';
import { useModelSpendingStore } from '@/stores/modelSpending';

const modelSpendingStore = useModelSpendingStore();

const periodStartLabel = computed(() =>
    formatDate(modelSpendingStore.spending?.period_start)
);
const periodEndLabel = computed(() =>
    formatDate(modelSpendingStore.spending?.period_end)
);

const resetsInLabel = computed(() => {
    const days = modelSpendingStore.daysUntilReset;
    if (days === null) return '-';
    if (days === 0) return 'Today';
    if (days === 1) return 'In 1 day';
    return `In ${days} days`;
});

const billingProviderLabel = computed(() => {
    const provider = modelSpendingStore.spending?.billing_provider;
    if (!provider) return '-';
    return provider.charAt(0).toUpperCase() + provider.slice(1);
});

const isUnavailable = computed(
    () => modelSpendingStore.state === 'unavailable'
);
const isLocked = computed(() => modelSpendingStore.state === 'locked');

const usageDescription = computed(() => {
    if (isLocked.value) {
        return "You've reached your model usage quota for this period.";
    }
    return "Track how much of your model usage quota you've consumed in the current billing period.";
});

const handleRefresh = () => {
    modelSpendingStore.refresh();
};

onMounted(() => {
    modelSpendingStore.ensureLoaded();
});
</script>

<template>
    <div class="title flex flex-col gap-6">
        <div class="flex items-start justify-between gap-4">
            <div class="flex flex-col gap-2">
                <h2 class="text-xl font-semibold">Model usage</h2>
                <p class="text-sm text-muted-foreground">
                    {{ usageDescription }}
                </p>
            </div>
            <Button
                variant="outline"
                size="sm"
                class="gap-2 shrink-0"
                :disabled="modelSpendingStore.loading"
                @click="handleRefresh"
            >
                <RotateCw
                    class="size-4"
                    :class="{ 'animate-spin': modelSpendingStore.loading }"
                />
                Refresh
            </Button>
        </div>

        <div
            v-if="isUnavailable"
            class="rounded-md border bg-muted/40 px-4 py-6 text-sm text-muted-foreground"
        >
            Usage information is temporarily unavailable. Please try again
            later.
        </div>

        <template v-else>
            <div class="space-y-3">
                <div class="flex items-baseline justify-between">
                    <span
                        class="text-sm font-medium leading-none text-foreground"
                    >
                        Used this period
                    </span>
                    <span
                        class="text-2xl font-semibold tabular-nums"
                        :class="{ 'text-destructive': isLocked }"
                    >
                        {{ modelSpendingStore.usedPercentage }}%
                    </span>
                </div>
                <Progress
                    :model-value="
                        Math.min(100, modelSpendingStore.usedPercentage)
                    "
                    class="h-2"
                />
            </div>

            <div class="general-information grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div class="space-y-2">
                    <span
                        class="text-xs font-medium leading-none text-muted-foreground"
                    >
                        Period start
                    </span>
                    <div
                        class="min-h-10 rounded-md border bg-muted/40 px-3 py-2 text-sm flex items-center"
                    >
                        {{ periodStartLabel }}
                    </div>
                </div>

                <div class="space-y-2">
                    <span
                        class="text-xs font-medium leading-none text-muted-foreground"
                    >
                        Period end
                    </span>
                    <div
                        class="min-h-10 rounded-md border bg-muted/40 px-3 py-2 text-sm flex items-center"
                    >
                        {{ periodEndLabel }}
                    </div>
                </div>

                <div class="space-y-2">
                    <span
                        class="text-xs font-medium leading-none text-muted-foreground"
                    >
                        Resets
                    </span>
                    <div
                        class="min-h-10 rounded-md border bg-muted/40 px-3 py-2 text-sm flex items-center"
                    >
                        {{ resetsInLabel }}
                    </div>
                </div>

                <div class="space-y-2">
                    <span
                        class="text-xs font-medium leading-none text-muted-foreground"
                    >
                        Billing
                    </span>
                    <div
                        class="min-h-10 rounded-md border bg-muted/40 px-3 py-2 text-sm flex items-center"
                    >
                        {{ billingProviderLabel }}
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
